import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://smartpanda.cloud';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const SOCIAL_LINKS = [
  'https://www.facebook.com/smartpanda.cloud/',
  'https://www.instagram.com/smartpanda.cloud/',
  'https://www.tiktok.com/@smartpanda.education',
];

const normalizeCanonical = (canonical) => {
  if (!canonical) return SITE_URL;

  if (
    canonical.startsWith('http://') ||
    canonical.startsWith('https://')
  ) {
    return canonical;
  }

  return `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}`;
};

const SEO = ({
  title = 'SmartPanda – Mokomieji žaidimai vaikams su AI',
  description =
    'Interaktyvūs mokomieji žaidimai vaikams nuo 4 iki 14 metų. Mokykitės matematikos, lietuvių kalbos, skaitymo ir kitų dalykų žaisdami su AI.',
  canonical = '/',
  image = DEFAULT_IMAGE,
  robots = 'index, follow',
  type = 'website',
  imageAlt = 'SmartPanda – mokomieji žaidimai vaikams',
  includeOrganizationSchema = true,
  includeWebsiteSchema = true,
  breadcrumbs = [],
  faq = [],
  games = [],
  pageType = 'WebPage',
  about = [],
  learningOutcomes = [],
}) => {
  const canonicalUrl = normalizeCanonical(canonical);

  /*
   * SEOCategoryPages currently passes "about" as a text string.
   * Normalize it safely so SEO rendering never crashes when
   * about is a string instead of an array.
   */
  const aboutItems = Array.isArray(about)
    ? about
    : about
      ? [about]
      : [];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'SmartPanda',
    url: SITE_URL,
    logo: `${SITE_URL}/android-chrome-512x512.png`,
    sameAs: SOCIAL_LINKS,
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'SmartPanda',
    url: SITE_URL,
    description:
      'Interaktyvūs mokomieji žaidimai vaikams nuo 4 iki 14 metų.',
    inLanguage: 'lt-LT',
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': pageType,
    '@id': `${canonicalUrl}#${pageType.toLowerCase()}`,
    name: title,
    url: canonicalUrl,
    description,
    inLanguage: 'lt-LT',
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
    ...(aboutItems.length > 0
      ? {
          about: aboutItems.map((item) => {
            const topic =
              typeof item === 'string'
                ? item
                : item?.name || item?.title || String(item);

            return {
              '@type': 'Thing',
              name: topic,
            };
          }),
        }
      : {}),
    ...(learningOutcomes.length > 0
      ? {
          teaches: learningOutcomes,
        }
      : {}),
  };

  const breadcrumbSchema =
    breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: normalizeCanonical(item.path ?? item.url),
          })),
        }
      : null;

  const faqSchema =
    faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  const itemListSchema =
    games.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `${title} – žaidimai`,
          numberOfItems: games.length,
          itemListElement: games.map((game, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: game.name,
            url: `${SITE_URL}/zaidimai/${game.slug}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang="lt" />

      <title>{title}</title>

      <meta name="description" content={description} />
      <meta name="robots" content={robots} />

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SmartPanda" />
      <meta property="og:locale" content="lt_LT" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {includeOrganizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {includeWebsiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}

      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {itemListSchema && (
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
